<template>
  <SettingsPageLayout>
    <div
      v-for="request of pendingChangeRequests"
      :key="request.id"
      class="bg-yellow-100 text-yellow-900 p-4 lg:flex items-center justify-left gap-2 rounded-lg my-8 text-center lg:text-left"
    >
      <vue-feather type="alert-triangle" class="h-8 mx-auto lg:mx-0" />
      <div class="lg:ml-8">
        <header class="font-bold">An advertising change request is pending</header>
        <p class="mt-1">
          An advertising change request from ${{ totalSpend }} to ${{ request.total_spend }} is pending. Once the change
          has been approved the new amount will reflect in Humaniz and an email will be sent.
        </p>
        <p class="mt-1">({{ request.reason }})</p>
      </div>
    </div>
    <div class="flex justify-end mb-2">
      <div>
        <TButtonSvg label="Advertising Change Request" color="white" @click="showAdvertisingChangeRequest = true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" />
            <circle cx="12" cy="12" r="9" />
            <line x1="9" y1="12" x2="15" y2="12" />
            <line x1="12" y1="9" x2="12" y2="15" />
          </svg>
        </TButtonSvg>
      </div>
    </div>
    <div v-if="loaded" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Indeed</h3>
              </div>
              <div>
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  ${{ indeed }} per month
                </span>
              </div>
            </div>
            <p class="mt-2 text-sm text-gray-500">
              Indeed, Inc. is an American worldwide employment website for job listings launched in November 2004. It is
              a subsidiary of Japan's Recruit Holdings and is headquartered in Austin, Texas, and Stamford, Connecticut,
              with additional offices around the world.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">ZipRecruiter</h3>
              </div>
              <div>
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  ${{ zipRecruiter }} per month
                </span>
              </div>
            </div>
            <p class="mt-2 text-sm text-gray-500">
              ZipRecruiter is an American employment marketplace for job seekers and employers. The company is
              headquartered in Santa Monica, California with offices in Tempe, AZ; London, UK and Tel Aviv, Israel.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Monster</h3>
              </div>
              <div>
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  ${{ monster }} per month
                </span>
              </div>
            </div>
            <p class="mt-2 text-sm text-gray-500">
              Monster.com is a global employment website that was established in 1999 as a result of the merger between
              The Monster Board and Online Career Centre. It is a subsidiary of Randstad Holding, a Dutch multinational
              human resource consulting firm. The company's headquarters are in Weston, Massachusetts.
            </p>
          </div>
        </div>
      </Card>

      <Card v-if="meta > 0">
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Meta</h3>
              </div>
              <div>
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  ${{ meta }} per month
                </span>
              </div>
            </div>
            <p class="mt-2 text-sm text-gray-500">
              Meta Platforms, Inc., doing business as Meta, and formerly named Facebook, Inc., and TheFacebook, Inc., is
              an American multinational technology conglomerate based in Menlo Park, California. The company owns and
              operates Facebook, Instagram, Threads, and WhatsApp, among other products and services.
            </p>
          </div>
        </div>
      </Card>
    </div>

    <AdvertisingHistory class="mt-6" :history="budgetHistory" />

    <AdvertisingChangeRequest
      v-if="showAdvertisingChangeRequest"
      :currentTotalSpend="totalSpend"
      @handleCancel="showAdvertisingChangeRequest = false"
      @handleSave="
        showAdvertisingChangeRequest = false;
        getPendingAdvertisingChangeRequests();
      "
    />
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import Card from '../../Card/index.vue';
import AdvertisingChangeRequest from './AdvertisingChangeRequest.vue';
import AdvertisingHistory from './AdvertisingHistory.vue';

export default {
  components: {
    SettingsPageLayout,
    Card,
    AdvertisingChangeRequest,
    AdvertisingHistory,
  },
  data() {
    return {
      loaded: true,
      showAdvertisingChangeRequest: false,
      indeed: 0,
      zipRecruiter: 0,
      monster: 0,
      meta: 0,
      pendingChangeRequests: [],
      budgetHistory: [],
    };
  },
  mounted() {
    this.getAdvertising();
    this.getPendingAdvertisingChangeRequests();
  },
  methods: {
    getAdvertising() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/monthly_marketing_budgets`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          if (data.active_budget != null) {
            self.indeed = data.active_budget.indeed_spend;
            self.zipRecruiter = data.active_budget.zip_spend;
            self.monster = data.active_budget.monster_spend;
            self.meta = data.active_budget.meta_spend;
          }
          for (let budget of data.records) {
            self.budgetHistory.push({
              start_date: budget.start_date,
              end_date: budget.end_date,
              indeed_spend: budget.indeed_spend,
              zip_spend: budget.zip_spend,
              monster_spend: budget.monster_spend,
              meta_spend: budget.meta_spend,
              total_spend: budget.indeed_spend + budget.zip_spend + budget.monster_spend + budget.meta_spend,
            });
          }
        });
    },
    getPendingAdvertisingChangeRequests() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/monthly_marketing_budget_update_requests?status=pending`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.pendingChangeRequests = data.records;
        });
    },
  },
  computed: {
    totalSpend() {
      return this.indeed + this.zipRecruiter + this.monster + this.meta;
    },
  },
};
</script>
